import {
  ClosedApplicationTile,
  DecisionningErrorTile,
  ErrorPage,
  Loader,
  useGetCreditApplicationById,
  useGetSkipBankRequestRequirements,
  useSubmissionProgress,
} from '@applyonline/shared'
import { scrollToTop } from '@applyonline/shared/utils'
import { EFinancingProgram } from '@if/core/types'
import { extractNumbersFromMerchantId } from '@if/core/utils'
import { useEffect, useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useRequiredDocumentsWithStatus } from '../../data/credit-application-hooks'
import CompletedAppPage from './CompletedApp/CompletedAppPage'
import HardHitApprovedResultPage from './HardHitApprovedResultPage'
import HardHitUnapprovedResultPage from './HardHitUnapprovedResultPage'
import { canPollCreditApp, computeHighLevelPage, isServerAnalysingCredit } from './credit-hooks'

const RouteToStatePage = () => {
  const { t } = useTranslation()
  const allParams = useParams()
  const id = allParams.id!
  const [shouldPoll, setShouldPoll] = useState(false)
  const [creditApp, isFetchingCreditApp] = useGetCreditApplicationById(
    id,
    EFinancingProgram.MechanicalWarranty,
    shouldPoll,
  ) // if true, poll every 5 secs
  const [skipBankRequiredDocuments, isFetchingReqDocsForSkip] = useGetSkipBankRequestRequirements(
    id,
    EFinancingProgram.MechanicalWarranty,
  )
  const skipBankRequiredDocumentsWithStatus = useRequiredDocumentsWithStatus(
    skipBankRequiredDocuments,
    creditApp?.documents,
  )
  const merchantNum = creditApp?.merchantId ? extractNumbersFromMerchantId(creditApp.merchantId) : ''
  const targetPath = `/${merchantNum}`
  const highLevelPage = computeHighLevelPage(creditApp, isFetchingCreditApp)
  const { progress, messageKey } = useSubmissionProgress(creditApp)

  useEffect(() => {
    if (creditApp) {
      const should = isServerAnalysingCredit(creditApp) && canPollCreditApp(creditApp)
      setShouldPoll(should)
    }
  }, [creditApp])

  useLayoutEffect(scrollToTop, [])

  if (isFetchingReqDocsForSkip || isFetchingCreditApp || highLevelPage === 'loading') {
    return (
      <div>
        <Loader title={t('common.withYouShortly')} description={t('common.pleaseWait')} />
      </div>
    )
  }

  return (
    <>
      {highLevelPage === 'notFound' && <ErrorPage title={t('common.notFound')} />}

      {highLevelPage === 'analysis-running' && (
        <div>
          <Loader title={t('finalDecision.processing')} description={t(messageKey as 'loader.submitting')} progression={progress} />
        </div>
      )}

      {highLevelPage === 'decisionning-error' && <DecisionningErrorTile />}

      {highLevelPage === 'hardhit-result' && <HardHitUnapprovedResultPage creditApp={creditApp!} />}

      {highLevelPage === 'approved' && (
        <HardHitApprovedResultPage
          creditApp={creditApp!}
          skipBankAccountRequiredDocuments={skipBankRequiredDocumentsWithStatus}
        />
      )}
      {highLevelPage === 'closed' && <ClosedApplicationTile status={creditApp!.status} targetPath={targetPath} />}

      {highLevelPage === 'funding' && <CompletedAppPage creditApp={creditApp!} />}
    </>
  )
}

export default RouteToStatePage
